/**
 * Automation Requests Store (Solicitudes tab — TV1-2377 slice 7)
 */

import { useCallback } from 'react';
import { createStore } from '@commandDrivenStateEngine/core/createStore';
import { Policy } from '@commandDrivenStateEngine/core/policies';
import type { CommandMetaInput, Dispatch } from '@commandDrivenStateEngine/core/types';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import useAuth from '@contexts/useAuth/useAuth';
import { initialState, RequestsState } from './state';
import { handlers } from './handlers';
import { invariants } from './invariants';
import { RequestsCommand } from './commands';
import {
  createRequestsQueryPolicy,
  createShadowEvidencePolicy,
  createPromotePolicy,
  createRecordInputValuesPolicy
} from './policies';

export { selectors, canPromoteSubGoal } from './invariants';

const store = createStore({
  name: 'automation-requests',
  initialState,
  handlers,
  invariants
});

export const {
  useSelector: useRequestsSelector,
  useErrors: useRequestsErrors,
  usePolicies: useRequestsPolicies,
  getState: getRequestsState,
  dispatch: dispatchRequestsCommand
} = store;

/** Attaches the authenticated user so `canApply`'s capability gate can authorize. */
export const useRequestsDispatch = (): Dispatch<RequestsCommand> => {
  const auth = useAuth();
  const user = auth?.user ?? null;
  return useCallback<Dispatch<RequestsCommand>>(
    ( command, metaInput: CommandMetaInput ) =>
      store.dispatch(
        command,
        metaInput.source === 'user' && metaInput.user === undefined
          ? { ...metaInput, user }
          : metaInput
      ),
    [user]
  );
};

export const createRequestsPolicies = (
  backend: ClientLibraryType
): Policy<RequestsState, RequestsCommand>[] => [
  createRequestsQueryPolicy( backend ),
  createShadowEvidencePolicy( backend ),
  createPromotePolicy( backend ),
  createRecordInputValuesPolicy( backend )
];
